import { useMutation, useQuery, useQueryClient } from 'react-query';
import { httpRequest } from '../config/api';

const QK = {
  list: 'lookup-tables/list',
  system: 'lookup-tables/system',
  custom: 'lookup-tables/custom'
};

const service = {
  getList: periodId =>
    httpRequest.get('api/lookup-tables/list', { params: { period_id: periodId } }),
  getSystem: reportType =>
    httpRequest.get('api/lookup-tables/system', { params: { report_type: reportType } }),
  getCustom: periodId =>
    httpRequest.get('api/lookup-tables/custom', { params: { period_id: periodId } }),
  createCustom: ({ periodId, data }) =>
    httpRequest.post('api/lookup-tables/custom', data, { params: { period_id: periodId } }),
  updateCustom: ({ tableId, data }) => httpRequest.put(`api/lookup-tables/custom/${tableId}`, data),
  deleteCustom: ({ tableId }) => httpRequest.delete(`api/lookup-tables/custom/${tableId}`),
  uploadCustom: ({ periodId, file, name }) => {
    const formData = new FormData();
    formData.append('file', file);
    return httpRequest.post('api/lookup-tables/custom/upload', formData, {
      params: { period_id: periodId, table_name: name },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const useLookupTablesList = (periodId, queryOptions = {}) =>
  useQuery([QK.list, periodId], () => service.getList(periodId), {
    enabled: !!periodId,
    ...queryOptions
  });

export const useSystemLookupTables = (reportType, queryOptions = {}) =>
  useQuery([QK.system, reportType], () => service.getSystem(reportType), {
    enabled: !!reportType,
    ...queryOptions
  });

export const useCustomLookupTables = (periodId, queryOptions = {}) =>
  useQuery([QK.custom, periodId], () => service.getCustom(periodId), {
    enabled: !!periodId,
    ...queryOptions
  });

const invalidate = (queryClient, periodId) => {
  queryClient.invalidateQueries([QK.custom, periodId]);
  queryClient.invalidateQueries([QK.list, periodId]);
};

export const useCreateCustomLookupTable = (periodId, mutationOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation(payload => service.createCustom({ periodId, data: payload }), {
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidate(queryClient, periodId);
      mutationOptions.onSuccess?.(...args);
    }
  });
};

export const useUpdateCustomLookupTable = (periodId, mutationOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation(({ tableId, data }) => service.updateCustom({ tableId, data }), {
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidate(queryClient, periodId);
      mutationOptions.onSuccess?.(...args);
    }
  });
};

export const useDeleteCustomLookupTable = (periodId, mutationOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation(({ tableId }) => service.deleteCustom({ tableId }), {
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidate(queryClient, periodId);
      mutationOptions.onSuccess?.(...args);
    }
  });
};

export const useUploadCustomLookupTable = (periodId, mutationOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation(({ file, name }) => service.uploadCustom({ periodId, file, name }), {
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidate(queryClient, periodId);
      mutationOptions.onSuccess?.(...args);
    }
  });
};
